import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { ResetModal } from "../../../../return/return-modals/reset-modal";
import {
  parseResetReturnPresentation,
  parseResetReturnRecommendation,
} from "./parse-reset-return-recommendation";

interface ResetReturnInput {
  returnId?: string;
}

function readInput(input: unknown): ResetReturnInput {
  return typeof input === "object" && input !== null ? input : {};
}

export interface ReturnResetCardProps {
  input: unknown;
  output: unknown;
  toolCallId: string;
  disabled: boolean;
}

export const ReturnResetCard = memo(function ReturnResetCard({
  input,
  output,
  disabled,
}: ReturnResetCardProps) {
  const args = readInput(input);
  const returnId = args.returnId;
  const presentation = parseResetReturnPresentation(output);
  const recommendation = parseResetReturnRecommendation(output);
  const [modalOpen, setModalOpen] = useState(false);
  const [resetFinished, setResetFinished] = useState(false);

  if (!returnId || !presentation) {
    return null;
  }

  return (
    <Flex dir="column" gap="sm" pb="md" w="full">
      {resetFinished ? (
        <Flex
          bgColor="primary"
          borderColor="secondary"
          borderRadius="md"
          borderStyle="solid"
          borderWidth="1px"
          dir="row"
          gap="sm"
          p="lg"
        >
          <RedoIcon color="success" Icon={CheckCircleIcon} size={18} />
          <Flex dir="column" gap="xxs" minW="none">
            <Text fontSize="sm" fontWeight="semibold">
              Return reset
            </Text>
            <Text fontSize="xs" textColor="secondary">
              The customer can start this return over from the return portal.
            </Text>
          </Flex>
        </Flex>
      ) : (
        <>
          {recommendation ? (
            <Flex
              bgColor="primary"
              borderColor="secondary"
              borderRadius="md"
              borderStyle="solid"
              borderWidth="1px"
              dir="column"
              gap="sm"
              p="lg"
            >
              <Text fontSize="xs" textColor="secondary">
                Recommended reset point
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                {recommendation.recommendedLabel}
              </Text>
              <Text fontSize="sm" textColor="secondary">
                {recommendation.customerImpact}
              </Text>
              {recommendation.differsFromDefault ? (
                <Text fontSize="sm" textColor="secondary">
                  {recommendation.whyNotDefault
                    ? `This is not the store default (${recommendation.defaultLabel}). ${recommendation.whyNotDefault}`
                    : `This is not the store default (${recommendation.defaultLabel}).`}
                </Text>
              ) : null}
            </Flex>
          ) : null}
          <RedoButton
            disabled={disabled}
            hierarchy="primary"
            onClick={() => setModalOpen(true)}
            size="md"
            text="Review & reset return"
          />
        </>
      )}
      {modalOpen ? (
        <ResetModal
          flowId={presentation.flowId}
          initialStep={recommendation?.recommendedStep}
          onClose={() => setModalOpen(false)}
          onSubmitFinished={() => {
            setModalOpen(false);
            setResetFinished(true);
          }}
          open={modalOpen}
          resource={{ returnId, returnType: presentation.returnType }}
        />
      ) : null}
    </Flex>
  );
});
